/**
 * Service-worker registration, and the only place in the app that touches it.
 *
 * The bug this exists to fix: vite-plugin-pwa's injected `registerSW.js` registers the
 * worker and then listens for nothing. A new build would install and take over in the
 * background while the page in front of the player still rendered the *old* precache, so
 * the update only became visible on the launch after the one that downloaded it — and an
 * installed PWA that the OS resumes rather than re-navigates might not get that launch
 * for days. Registering through `virtual:pwa-register` is what makes the update
 * observable; `registerType: 'prompt'` in vite.config.ts is what makes *when* to apply it
 * our decision rather than a race with the download.
 *
 * Nothing here runs inside the frame loop. The store writes are discrete events.
 */

import { registerSW } from 'virtual:pwa-register';
import { useGameStore } from '../store/gameStore';
import { decideUpdateAction } from './updatePolicy';

/** Backstop for a session left open across a deploy without ever being backgrounded. */
const UPDATE_CHECK_INTERVAL_MS = 30 * 60 * 1000;

/** A player flipping between apps must not produce a request per flip. */
const MIN_CHECK_SPACING_MS = 60 * 1000;

/**
 * Survives the reload it guards against, and dies with the tab — which is exactly the
 * lifetime a "we already reloaded once" brake needs. Not store state: it must outlive a
 * page load, and it is not game state.
 */
const AUTO_APPLIED_KEY = 'lab17:update-auto-applied';

/** Private browsing and locked-down settings can make sessionStorage throw on access. */
function hasAutoApplied(): boolean {
  try {
    return sessionStorage.getItem(AUTO_APPLIED_KEY) === 'true';
  } catch {
    // Unreadable storage means no brake. Treating that as "already applied" would
    // disable silent updates entirely; treating it as "not yet" costs at most one extra
    // reload in a failure mode that also requires a half-synced deploy.
    return false;
  }
}

function rememberAutoApplied(): void {
  try {
    sessionStorage.setItem(AUTO_APPLIED_KEY, 'true');
  } catch {
    // Nothing to do — see above.
  }
}

export function registerUpdates(): void {
  if (!('serviceWorker' in navigator)) return;

  const updateSW = registerSW({
    onNeedRefresh() {
      // Fires only when a worker is already in control, i.e. a genuine update rather
      // than a first install.
      const action = decideUpdateAction(useGameStore.getState().phase, hasAutoApplied());

      if (action === 'apply') {
        // Set the brake *before* reloading: the page that comes back must be able to see
        // that an automatic apply already happened.
        rememberAutoApplied();
        void updateSW(true);
        return;
      }

      useGameStore.getState().markUpdateReady(() => {
        void updateSW(true);
      });
    },

    onRegisteredSW(_swUrl, registration) {
      if (registration) watchForUpdates(registration);
    },
  });
}

/**
 * Ask, at launch and then throughout the session.
 *
 * Registering is *not* enough to get a check, which is worth stating plainly because
 * assuming otherwise is what produced the original bug. The browser's own update check
 * on an existing registration is a "soft update" it is free to skip, and Chromium does
 * skip it: driving this by registration alone, a deploy went undetected for the whole
 * session, while an explicit `update()` on the same page found the new worker in
 * seconds. So the check is made explicitly, on purpose, at a moment we choose.
 */
function watchForUpdates(registration: ServiceWorkerRegistration): void {
  let lastCheck = 0;

  const check = (): void => {
    const now = Date.now();
    if (now - lastCheck < MIN_CHECK_SPACING_MS) return;
    lastCheck = now;

    // Rejects when offline, which is normal and not something to put in front of a
    // player. One conditional GET of a ~2 KB file; nothing the frame budget notices.
    registration.update().catch(() => {});
  };

  // At launch: this is what makes a deploy visible on the launch after it, rather than
  // the one after that.
  //
  // Except on a first-ever visit, where there is no controller and the worker was
  // fetched seconds ago — nothing can have changed, and checking anyway would put a
  // redundant request and install in the way of a boot that is already precaching the
  // whole game.
  if (navigator.serviceWorker.controller) check();

  // The one that matters for an installed game, which the OS resumes without a
  // navigation and therefore without any check of the browser's own.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') check();
  });

  setInterval(check, UPDATE_CHECK_INTERVAL_MS);
}
